import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  NgModule,
  ElementRef,
  ViewChild,
} from '@angular/core';
import { CdkPortal, DomPortalOutlet, PortalModule } from '@angular/cdk/portal';
import { SessionService } from '../../services/session.service';
import { Facility } from '../../models/facility';
import { SessionData } from '../../models/session';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { Address } from '../../models/address';
import { CommonModule } from '@angular/common';
import {
  DxBulletModule,
  DxButtonModule,
  DxDataGridModule,
  DxPopupModule,
  DxSelectBoxModule,
  DxTextBoxModule,
  DxListModule,
} from 'devextreme-angular';
import { MsTooltipModule } from '../ms-tooltip/ms-tooltip.component';
import { UserService } from '../../services/user/user.service';
import { PharmacyService } from '../../services/user/pharmacy.service';
import { NotificationService } from '../../services/notification.service';
import _ from 'lodash';
import { OrdersService } from '../../services/order/orders.service';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import * as moment from 'moment';
import { DxoTooltipModule } from 'devextreme-angular/ui/nested';
import { OverlayModule } from '../overlay/overlay.component';
import { environment } from 'src/environments/environment';
import { HttpParams } from '@angular/common/http';
import CustomStore from 'devextreme/data/custom_store';
import { ScreenService } from 'src/app/shared/services/screen.service';
import { FacilityCardComponent } from './facility-card/facility-card.component';
import DataSource from 'devextreme/data/data_source';
import { FeatureFlagsService } from 'src/app/shared/services/featureFlags.service';
import { AppInfoService } from 'src/app/shared/services/app-info.service';

@Component({
  selector: 'app-switch-facilities',
  templateUrl: './switch-facilities.component.html',
  styleUrls: ['./switch-facilities.component.scss'],
})
export class SwitchFacilitiesComponent implements OnInit, OnDestroy {
  @Input() readOnly = false;
  @Input() mode; //profile, header, link
  @Input() items: any[];
  @Input() isReadOnly = false;

  subscriptions: Array<Subscription> = [];
  profile: any;
  session: SessionData;
  primaryFacility: any;
  primaryFacilityId: number;
  company_id: number;
  isCompanyAdmin = false;
  changeFacility = false;
  isAdmin = false;
  // Sales rep OR customer rep — the backend's is_representative. Gates the
  // "Ordering for:" header, which both rep kinds get.
  isRepresentative = false;
  // Sales rep only. The territory-view scoping is applied server-side under
  // is_sales_representative, so a customer rep must not get its control.
  isSalesRepresentative = false;
  isVetted = false;
  canSwitch = false;
  facilitiesList: Array<Facility> = [];
  deliveryAddress: Address;
  private onDestroy$: Subject<void> = new Subject<void>();
  private search$ = new Subject<void>();

  // The panel is position:fixed with z-index 999, but inside the fixed header
  // toolbar (z-index 2) that value is scoped to the toolbar's stacking context
  // and loses to the past-due banner (998). Portaling it to <body> lets its
  // z-index compete at root level (MDG-106).
  @ViewChild(CdkPortal) private facilityPanelPortal: CdkPortal;
  private facilityPanelOutlet = new DomPortalOutlet(document.body);
  private _showFacilityInfo = false;

  get showFacilityInfo(): boolean {
    return this._showFacilityInfo;
  }

  set showFacilityInfo(value: boolean) {
    if (value === this._showFacilityInfo) {
      return;
    }
    this._showFacilityInfo = value;
    if (value) {
      this.facilityPanelOutlet.attach(this.facilityPanelPortal);
    } else {
      this.facilityPanelOutlet.detach();
    }
  }

  tooltipContent: string;
  target: any;
  isVisible = false;
  eventListenersAdded = false;
  budgetAmountUsed: any;
  budgetPercentage: any;
  savingsAmountUsed: any;
  savingsPercentage: any;
  budgetEndDate: Date;
  savingsEndDate: Date;
  formattedBudgetEnd: any;
  formattedBudgetStart: any;
  canManageBudget: any;
  canSetBudgetRoles = [
    'Administrator',
    'CompanyAdmin',
    'FinanceAdmin',
    'FacilityAdmin',
  ];
  hydraEnabled = environment.hydraEnabled;

  today: Date = new Date();
  showPharmacyBudgetModal = false;

  budget = {
    target: '',
    amount: 0,
    start_date: '',
    end_date: '',
  };
  savings = {
    target: '',
    amount: 0,
    start_date: '',
    end_date: '',
  };

  companySpentBudget: any;
  companySavingsBudget: any;
  facilitySpentBudget: any;
  facilitySavingsBudget: any;

  facilitiesDataLoaded = false;
  facilitiesDataSource: DataSource;
  facilitiesStore: CustomStore;
  searchText: string = '';
  termsPendingOnly = false;
  termsToolsEnabled = false;
  // 'own' = the rep's owned territories (default), 'open' = open territories.
  // Mutually exclusive views, not an additive filter (MDG-290).
  territoryView: 'own' | 'open' = 'own';

  constructor(
    public appInfo: AppInfoService,
    private elementRef: ElementRef,
    public sessionService: SessionService,
    private userService: UserService,
    public pharmacyService: PharmacyService,
    private ordersService: OrdersService,
    private notifyService: NotificationService,
    private router: Router,
    private route: ActivatedRoute,
    public screen: ScreenService,
    public featureFlagsService: FeatureFlagsService,
  ) {}

  async ngOnInit() {
    this.search$
      .pipe(debounceTime(300), takeUntil(this.onDestroy$))
      .subscribe(() => this.applySearch());
    await this.getProfileInformation();
    this.termsToolsEnabled =
      !!this.featureFlagsService.getByEnv('process', 'terms_acceptance_gate') ||
      !!this.featureFlagsService.getByEnv('process', 'soft_terms_acceptance_gate');
    if (this.mode === 'page') {
      this.searchText =
        this.route.snapshot.queryParamMap.get('facility_search') || '';
      this.termsPendingOnly =
        this.route.snapshot.queryParamMap.get('terms_pending') === '1';
      this.territoryView =
        this.route.snapshot.queryParamMap.get('territory_view') === 'open'
          ? 'open'
          : 'own';
      this.createFacilityDataSource();
    } else {
      this.getPharmacies();
    }
    this.watchPrimaryFacilityUpdatedTrigger();
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.onDestroy$.next();
    this.onDestroy$.complete();
    // detach only — dispose() would remove the outlet element (document.body)
    this.facilityPanelOutlet.detach();
  }

  get isMobile() {
    return (
      this.screen.sizes['screen-x-small'] || this.screen.sizes['screen-small']
    );
  }

  async getProfileInformation() {
    const res = await this.userService.getMe().toPromise();
    this.populateProfileData(res);
  }

  private populateProfileData(res: any) {
    this.profile = res.data;
    this.isCompanyAdmin = this.profile.is_company_admin;
    this.changeFacility = this.profile.roles.includes('ChangeFacility');
    this.isAdmin = this.profile.is_admin;
    this.isSalesRepresentative = this.profile.is_sales_representative;
    this.isRepresentative =
      this.profile.is_sales_representative ||
      this.profile.is_customer_representative;
    this.isVetted = this.profile.is_vetted;
    this.primaryFacility = this.profile.primary_facility;
    this.deliveryAddress = this.primaryFacility?.delivery_address;
    this.primaryFacilityId = this.primaryFacility?.id;
    this.company_id = this.profile.company_id;
    this.canSwitch =
      !this.readOnly &&
      (this.isCompanyAdmin ||
        this.changeFacility ||
        this.isAdmin ||
        this.profile.is_sales_representative);
    this.populateBudgetData(this.profile);
    this.canManageBudget = this.canSetBudgetRoles.some(role =>
      this.profile?.roles?.includes(role),
    );

    if (this.budget.amount || this.savings.amount) {
      this.getOrdersFromDate();
      this.budgetEndDate = new Date(this.budget.end_date);
      // savings is locked to the budget period; savings rows carry no dates
      this.savingsEndDate =
        this.savings.amount && this.budget.end_date
          ? new Date(this.budget.end_date)
          : null;
    } else {
      this.budgetEndDate = null;
      this.savingsEndDate = null;
    }
  }

  getPharmacies() {
    const s = this.pharmacyService
      .getPharmacies({ with_company: 0, with_buyers: 0, with_documents: 0 })
      .subscribe(
        result => (this.facilitiesList = result.data),
        error => this.notifyService.showNotify(error, 'error'),
      );
    this.subscriptions.push(s);
  }

  onSearchTextChange(event: any) {
    this.searchText = event.value?.trim();
  }

  onSearchChange() {
    this.search$.next();
  }

  private applySearch() {
    if (this.mode === 'page') {
      this.router.navigate([], {
        queryParams: { facility_search: this.searchText?.trim() || null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
    const term = this.searchText?.trim();
    if (!term || term.length >= 2) {
      this.facilitiesDataSource.reload();
    }
  }

  onTermsPendingToggle() {
    this.termsPendingOnly = !this.termsPendingOnly;
    if (this.mode === 'page') {
      this.router.navigate([], {
        queryParams: { terms_pending: this.termsPendingOnly ? '1' : null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
    this.facilitiesDataSource.reload();
  }

  onTerritoryViewToggle() {
    this.territoryView = this.territoryView === 'open' ? 'own' : 'open';
    if (this.mode === 'page') {
      this.router.navigate([], {
        queryParams: {
          territory_view: this.territoryView === 'open' ? 'open' : null,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
    this.facilitiesDataSource.reload();
  }

  createFacilityDataSource() {
    this.facilitiesDataLoaded = false;
    this.facilitiesStore = new CustomStore({
      key: 'id',
      load: this.fetchData,
    });

    this.facilitiesDataSource = new DataSource({
      store: this.facilitiesStore,
    });
  }

  fetchData = loadOptions => {
    let params: HttpParams = new HttpParams();

    if (loadOptions?.take) {
      const page = Math.floor(loadOptions.skip / loadOptions.take + 1);
      params = params.set('page', JSON.stringify(page));
      params = params.set('per_page', JSON.stringify(loadOptions.take));
    } else {
      params = params.set('page', '1');
    }

    if (loadOptions?.sort?.length) {
      params = params.set('sort_by', loadOptions.sort[0].selector);
      params = params.set(
        'sort_order',
        loadOptions.sort[0].desc ? 'desc' : 'asc',
      );
    } else {
      params = params.set('sort_by', this.termsPendingOnly ? 'order_count' : 'name');
      params = params.set('sort_order', this.termsPendingOnly ? 'desc' : 'asc');
    }

    if (this.searchText) {
      params = params.set('search', this.searchText);
    }

    if (this.termsPendingOnly) {
      params = params.set('terms_pending', '1');
    }

    if (this.isSalesRepresentative) {
      params = params.set('territory_view', this.territoryView);
    }

    return this.pharmacyService
      .getFacilitiesFromES(params)
      .toPromise()
      .then(result => {
        this.facilitiesDataLoaded = true;
        this.facilitiesList = result.data;
        return { data: result.data, totalCount: result.meta.total || 0 };
      })
      .catch(() => {
        throw 'Facilities Data Loading Error';
      });
  };

  getFacilitiesFromES(params: any) {
    const s = this.pharmacyService.getFacilitiesFromES(params).subscribe(
      result => (this.facilitiesList = result.data),
      error => this.notifyService.showNotify(error, 'error'),
    );
    this.subscriptions.push(s);
  }

  onFacilityChanged(e) {
    const params = { facility_id: e.value, user_id: this.profile.id };
    this.setPrimaryFacility(params);
  }

  setPrimaryFacility(params: any, goToPage: string = null) {
    const s = this.userService.setPrimaryFacility(params).subscribe(
      () => {
        this.onFacilityChangeSuccess();
        if (goToPage) {
          this.router.navigate([goToPage]);
        }
      },
      error => this.onFacilityChangedError(error),
    );
    this.subscriptions.push(s);
  }

  private onFacilityChangeSuccess() {
    this.notifyService.showNotify('Primary facility was changed', 'success');
    this.sessionService.switchFacilityTrigger.next(false);
    this.sessionService.primaryFacilityUpdatedTrigger.next(true);
    this.showFacilityInfo = false;
  }

  private onFacilityChangedError(error: any) {
    this.notifyService.showNotify(error, 'error');
    this.primaryFacilityId = this.primaryFacility?.id;
  }

  watchPrimaryFacilityUpdatedTrigger() {
    const s = this.sessionService.primaryFacilityUpdatedTrigger.subscribe(
      async () => await this.getProfileInformation(),
    );
    this.subscriptions.push(s);
  }

  switchFacility() {
    this.sessionService.switchFacilityTrigger.next(true);
  }

  openFacilityDialog() {
    this.showFacilityInfo = !this.showFacilityInfo;
  }

  customizeBudgetTooltip(arg) {
    const formatter = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    });

    const budgetText =
      arg.target - arg.value > 0 ? 'Budget Remaining' : 'Budget Exceeded By';
    return {
      html:
        `<p class='text-left mb-0'>Budget Spent: \$${formatter.format(arg.value)} <br>` +
        `${budgetText}: \$${formatter.format(Math.abs(arg.target - arg.value))}</p>`,
    };
  }

  customizeSavingsTooltip(arg) {
    const formatter = new Intl.NumberFormat('en-US', {
      maximumFractionDigits: 0,
    });

    const savingsText =
      arg.target - arg.value > 0
        ? 'Savings Goal Remaining'
        : 'Savings Goal Exceeded By';
    return {
      html:
        `<p class='text-left mb-0'> Savings Earned: \$${formatter.format(arg.value)} <br>` +
        `${savingsText}: \$${formatter.format(arg.target - arg.value)}</p>`,
    };
  }

  getOrdersFromDate() {
    if (!this.budget.start_date) {
      return;
    }

    this.subscriptions.push(
      this.ordersService.getBudgetDataFromDate({}).subscribe(
        result => {
          this.budgetAmountUsed = result.data.total_cost;
          this.budgetPercentage = (
            (this.budgetAmountUsed / this.budget.amount) *
            100
          ).toFixed(0);

          this.savingsAmountUsed = result.data.total_savings;
          this.savingsPercentage = (
            (this.savingsAmountUsed / this.savings.amount) *
            100
          ).toFixed(0);
        },
        error => this.notifyService.showNotify(error, 'error'),
      ),
    );
  }

  populateBudgetData(result) {
    this.companySpentBudget = result.company?.budget.find(
      x => x.target === 'company' && x.type === 'spent',
    );
    this.companySavingsBudget = result.company?.budget.find(
      x => x.target === 'company' && x.type === 'savings',
    );
    this.facilitySpentBudget = result.primary_facility?.budget.find(
      x => x.target === 'facility' && x.type === 'spent',
    );
    this.facilitySavingsBudget = result.primary_facility?.budget.find(
      x => x.target === 'facility' && x.type === 'savings',
    );

    let tmpBudget =
      this.companySpentBudget || this.facilitySpentBudget || this.budget;
    let tmpSavings =
      this.companySavingsBudget || this.facilitySavingsBudget || this.savings;
    this.budget = tmpBudget;
    this.savings = tmpSavings;

    this.formattedBudgetStart = this.budget.start_date
      ? moment(this.budget.start_date, 'MM-DD-YYYY').format('MMM Do YY')
      : null;
    this.formattedBudgetEnd = this.budget.end_date
      ? moment(this.budget.end_date, 'MM-DD-YYYY').format('MMM Do YY')
      : null;
  }

  setTooltip(index, e) {
    this.tooltipContent = this.items[index].text;
    this.target = e.target;
    this.isVisible = typeof this.items[index].text !== 'undefined';
  }

  hideTooltip() {
    this.isVisible = false;
  }

  goToBudgetManager() {
    this.showFacilityInfo = false;
    this.router.navigate(['/pharmacies']);
  }
}

@NgModule({
  imports: [
    CommonModule,
    RouterModule,
    DxDataGridModule,
    DxPopupModule,
    DxSelectBoxModule,
    MsTooltipModule,
    DxTextBoxModule,
    DxBulletModule,
    DxButtonModule,
    DxoTooltipModule,
    OverlayModule,
    DxListModule,
    PortalModule,
  ],
  declarations: [SwitchFacilitiesComponent, FacilityCardComponent],
  exports: [SwitchFacilitiesComponent],
})
export class SwitchFacilitiesModule {}
